import { useCallback, useEffect, useState } from "react";
import { GET_STORE_CUSTOMERS } from "src/constants/queries";
import { GRAPHQL_ENDPOINT } from "src/constants/endpoints";
import { useAuth } from "src/contexts/auth-provider";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";
import {
  StoreCustomerEdge,
  StoreCustomerPageInfo,
  StoreCustomerSearchInput,
} from "src/interfaces/customer.interface";

const DEFAULT_PAGE_SIZE = 10;

export function useStoreCustomers(pageSize = DEFAULT_PAGE_SIZE) {
  const { axBe } = useAxiosInterceptor();
  const { hasRestaurant } = useAuth();

  const [edges, setEdges] = useState<StoreCustomerEdge[]>([]);
  const [pageInfo, setPageInfo] = useState<StoreCustomerPageInfo | null>(null);
  const [after, setAfter] = useState<string | null>(null);
  const [before, setBefore] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [cursorCache, setCursorCache] = useState<Record<number, string>>({});
  const [search, setSearch] = useState<StoreCustomerSearchInput>({});
  const [isLoading, setIsLoading] = useState(false);

  const fetchStoreCustomers = useCallback(
    async (
      cursorAfter: string | null,
      cursorBefore: string | null,
      pageWeAreLoading: number
    ) => {
      if (!hasRestaurant?.correlationId) return;
      setIsLoading(true);
      try {
        const variables: Record<string, unknown> = {
          storeCorrelationId: hasRestaurant.correlationId,
          search: Object.keys(search).length ? search : null,
        };
        if (cursorBefore) {
          variables.last = pageSize;
          variables.before = cursorBefore;
        } else {
          variables.first = pageSize;
          if (cursorAfter) variables.after = cursorAfter;
        }

        const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
          query: GET_STORE_CUSTOMERS,
          variables,
        });

        const connection = data?.data?.storeCustomers;
        const newEdges: StoreCustomerEdge[] = connection?.edges ?? [];
        const newPageInfo: StoreCustomerPageInfo | null =
          connection?.pageInfo ?? null;

        setEdges(newEdges);
        setPageInfo(newPageInfo);

        if (
          newPageInfo?.hasNextPage &&
          newPageInfo?.endCursor != null &&
          newPageInfo.endCursor !== ""
        ) {
          setCursorCache((prev) => ({
            ...prev,
            [pageWeAreLoading + 1]: newPageInfo.endCursor as string,
          }));
        }
      } catch (err) {
        console.error("Error fetching store customers:", err);
        setEdges([]);
        setPageInfo(null);
      } finally {
        setIsLoading(false);
      }
    },
    [hasRestaurant?.correlationId, pageSize, search, axBe]
  );

  useEffect(() => {
    if (hasRestaurant?.correlationId) {
      fetchStoreCustomers(after, before, currentPage);
    }
  }, [hasRestaurant?.correlationId, after, before, currentPage, search, fetchStoreCustomers]);

  const loadNextPage = useCallback(() => {
    if (isLoading) return;
    if (pageInfo?.hasNextPage && pageInfo?.endCursor) {
      setBefore(null);
      setAfter(pageInfo.endCursor);
      setCurrentPage((p) => p + 1);
    }
  }, [isLoading, pageInfo?.hasNextPage, pageInfo?.endCursor]);

  const loadPrevPage = useCallback(() => {
    if (isLoading) return;
    if (pageInfo?.hasPreviousPage && pageInfo?.startCursor) {
      setAfter(null);
      setBefore(pageInfo.startCursor);
      setCurrentPage((p) => Math.max(1, p - 1));
    }
  }, [isLoading, pageInfo?.hasPreviousPage, pageInfo?.startCursor]);

  const goToPage = useCallback(
    (page: number) => {
      if (isLoading) return;
      const target = Math.max(1, Math.min(page, pageInfo?.totalNumberOfPages ?? page));
      if (target === currentPage) return;
      if (target === 1) {
        setAfter(null);
        setBefore(null);
        setCurrentPage(1);
        return;
      }
      const cursor = cursorCache[target];
      if (cursor != null) {
        setAfter(cursor);
        setBefore(null);
        setCurrentPage(target);
      } else if (target === currentPage + 1 && pageInfo?.endCursor) {
        loadNextPage();
      }
    },
    [isLoading, cursorCache, currentPage, pageInfo?.totalNumberOfPages, pageInfo?.endCursor, pageInfo?.hasNextPage, loadNextPage]
  );

  const applySearch = useCallback((keyword: string) => {
    setSearch((prev) => ({
      ...prev,
      nameKeyword: keyword?.trim() || undefined,
    }));
    setAfter(null);
    setBefore(null);
    setCurrentPage(1);
    setCursorCache({});
  }, []);

  const totalPages = pageInfo?.totalNumberOfPages ?? 1;

  const canGoToPage = useCallback(
    (page: number) => {
      if (page < 1 || page > totalPages) return false;
      if (page === 1) return true;
      if (page === currentPage + 1) return pageInfo?.hasNextPage ?? false;
      return cursorCache[page] != null;
    },
    [totalPages, currentPage, cursorCache, pageInfo?.hasNextPage]
  );

  const refetch = useCallback(() => {
    if (hasRestaurant?.correlationId) {
      fetchStoreCustomers(after, before, currentPage);
    }
  }, [hasRestaurant?.correlationId, after, before, currentPage, fetchStoreCustomers]);

  return {
    edges,
    pageInfo,
    isLoading,
    currentPage,
    totalPages,
    goToPage,
    loadNextPage,
    loadPrevPage,
    applySearch,
    setSearch,
    canGoToPage,
    refetch,
  };
}
